import * as React from 'react';
import styled from 'styled-components';
import {
	brandingColourHeavyLight,
	cardInnerGap,
	cardLargeInnerGap,
	newContentPadding,
	superLightGreyColour,
	tabletPortraitMinWidth,
	whiteColour
} from '../../cssConstants';
import { ActionButton, LinkButton } from './LinkButton';
import { NewCardDescription, NewCardTitle, NewCardWrapper } from './CardLayout';

export interface ContentCardProps {
	image?: string;
	imageHeight?: string;
	imageObjectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
	imageBackgroundColor?: string;
	headerIcon?: string;
	title: string;
	subtitle?: string;
	metaData?: string;
	description?: string;
	descriptionExtra?: string;
	buttonText?: string;
	buttonTo?: string;
	buttonHref?: string;
	onButtonClick?: () => void;
	children?: React.ReactNode;
	tagText?: string;
	centred?: boolean;
	className?: string;
	imageAnchor?: string;
}

const ContentCard = ({
	image,
	imageHeight = '154px',
	imageObjectFit = 'cover',
	imageBackgroundColor,
	imageAnchor,
	headerIcon,
	title,
	subtitle,
	metaData,
	description,
	descriptionExtra,
	buttonText,
	buttonTo,
	buttonHref,
	onButtonClick,
	children,
	tagText,
	centred,
	className
}: ContentCardProps) => {
	const renderButton = () => {
		if (!buttonText) return null;

		if (buttonTo) {
			return <LinkButton to={buttonTo}>{buttonText}</LinkButton>;
		}
		if (buttonHref) {
			return <LinkButton href={buttonHref}>{buttonText}</LinkButton>;
		}
		if (onButtonClick) {
			return <ActionButton onClick={onButtonClick}>{buttonText}</ActionButton>;
		}
		return null;
	};

	// Padding logic: only one of NewCardWrapper or ContentSection provides padding to avoid double padding.
	// With image: NewCardWrapper has no padding (image spans full width), ContentSection provides padding.
	// Without image: NewCardWrapper provides padding, ContentSection has no padding.
	return (
		<NewCardWrapper className={className} $noPadding={!!image}>
			{image && (
				<ImageWrapper $height={imageHeight} $backgroundColor={imageBackgroundColor}>
					<CardImage src={image} alt={title} $objectFit={imageObjectFit} $imageAnchor={imageAnchor} />
					{tagText && <TagText>{tagText}</TagText>}
				</ImageWrapper>
			)}
			<ContentSection $noPadding={!image} $centred={centred} $innerGap={centred ? 'large' : 'small'}>
				{headerIcon && <HeaderIcon src={headerIcon} alt="header icon" />}
				<TitleWrapper>
					<NewCardTitle>{title}</NewCardTitle>
					{metaData && <MetaData>{metaData}</MetaData>}
				</TitleWrapper>
				{subtitle && <SubtitleText>{subtitle}</SubtitleText>}
				{description && <NewCardDescription $centred={centred}>{description}</NewCardDescription>}
				{descriptionExtra && <NewCardDescription $centred={centred}>{descriptionExtra}</NewCardDescription>}
				{children}
				{renderButton()}
			</ContentSection>
		</NewCardWrapper>
	);
};

export default ContentCard;

const TitleWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 7px;
`;

const ImageWrapper = styled.div<{ $height?: string; $backgroundColor?: string }>`
	position: relative;
	width: 100%;
	height: ${({ $height }) => $height || '154px'};
	background-color: ${({ $backgroundColor }) => $backgroundColor || 'transparent'};
	flex-shrink: 0;
`;

const CardImage = styled.img<{ $objectFit?: string; $imageAnchor?: string }>`
	width: 100%;
	height: 100%;
	object-fit: ${({ $objectFit }) => $objectFit || 'cover'};
	display: block;
	object-position: ${({ $imageAnchor }) => $imageAnchor || 'initial'};
`;

const HeaderIcon = styled.img`
	width: 80px;
	height: 80px;
	display: block;
`;

const ContentSection = styled.div<{ $noPadding?: boolean; $centred?: boolean; $innerGap?: 'small' | 'large' }>`
	display: flex;
	flex-direction: column;
	flex: 1;
	gap: ${({ $innerGap }) => {
		if ($innerGap === 'small') return cardInnerGap;
		return cardLargeInnerGap;
	}};
	padding: ${({ $noPadding }) => ($noPadding ? '0' : newContentPadding)};
	align-items: ${({ $centred }) => ($centred ? 'center' : 'flex-start')};
`;

const SubtitleText = styled(NewCardTitle)`
	font-size: 14px;
`;

const MetaData = styled.p`
	font-size: 11px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.5px;
	color: ${superLightGreyColour};
`;

const TagText = styled.span`
	position: absolute;
	bottom: ${newContentPadding};
	left: ${newContentPadding};
	display: flex;
	font-size: 8px;
	line-height: 11px;
	padding: 5px 8px;
	justify-content: center;
	align-items: center;
	border-radius: 11px;
	color: ${whiteColour};
	font-weight: 500;
	background-color: ${brandingColourHeavyLight};
	min-width: 50px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 10px;
		line-height: 14px;
	}
`;
